import { useEffect, useMemo, useState } from "react";
import { Download, FileBox, RefreshCcw, Search, Sparkles, UploadCloud } from "lucide-react";
import { api, apiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = ".jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,.pdf,.zip,.mp3,.wav";

function formatBytes(bytes) {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value.toFixed(1)} ${units[unit]}`;
}

function rowKey(item) {
  return `${item.record_type}-${item.record_id}-${item.role}`;
}

function downloadUrl(item, role = item.role) {
  return `${BACKEND}/api/admin/media/download/${item.record_type}/${item.record_id}/${role}`;
}

export function MediaLibrary({ events, onChanged, refreshKey }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [upload, setUpload] = useState({ event_id: "", title: "", file: null });
  const [busy, setBusy] = useState("");
  const [priceDrafts, setPriceDrafts] = useState({});
  const [riderDrafts, setRiderDrafts] = useState({});
  const [labeling, setLabeling] = useState("");
  const [labelProgress, setLabelProgress] = useState("");
  const [labelEditId, setLabelEditId] = useState("");
  const [labelDraft, setLabelDraft] = useState({ title: "", vehicle: "", tags: "" });
  const [selected, setSelected] = useState(new Set());
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkRider, setBulkRider] = useState("");

  async function fetchMedia() {
    setLoading(true);
    try {
      const response = await api.get("/admin/media");
      setItems(response.data);
    } catch (error) {
      toast.error(apiError(error, "Media library could not be loaded"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchMedia(); }, [refreshKey]);

  useEffect(() => {
    if (!events?.length) return;
    if (!eventFilter || !events.some((eventItem) => eventItem.id === eventFilter)) setEventFilter(events[0].id);
  }, [events]);

  useEffect(() => {
    setSelected(new Set());
  }, [eventFilter]);

  useEffect(() => {
    if (!upload.event_id && events?.length) setUpload((current) => ({ ...current, event_id: events[0].id }));
  }, [events]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((item) => {
      if (item.event_id !== eventFilter) return false;
      if (!term) return true;
      return [item.title, item.event_title, item.original_filename, item.content_type, item.role, item.status]
        .some((value) => (value || "").toLowerCase().includes(term));
    });
  }, [items, query, eventFilter]);

  const photoRows = useMemo(() => filtered.filter((item) => item.record_type === "photo" && item.role === "original"), [filtered]);
  const allSelected = photoRows.length > 0 && photoRows.every((item) => selected.has(item.record_id));

  function toggleSelect(recordId) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(recordId)) next.delete(recordId); else next.add(recordId);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(allSelected ? new Set() : new Set(photoRows.map((item) => item.record_id)));
  }

  async function applyBulkPrice() {
    const value = Number(bulkPrice);
    if (!value || value <= 0) return toast.error("Enter a valid price");
    if (!window.confirm(`Set ${selected.size} selected photo(s) to $${value.toFixed(2)} each?`)) return;
    setBusy("bulk-price");
    try {
      const response = await api.put("/admin/photos/bulk-price", { photo_ids: [...selected], price: value });
      toast.success(`Updated ${response.data.updated} photo(s) to $${value.toFixed(2)}`);
      setSelected(new Set());
      setBulkPrice("");
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function applyBulkRider() {
    const rider = bulkRider.trim();
    if (!rider && !window.confirm(`Clear the rider/driver tag on ${selected.size} selected photo(s)?`)) return;
    setBusy("bulk-rider");
    try {
      const response = await api.put("/admin/photos/bulk-rider", { photo_ids: [...selected], rider });
      toast.success(rider ? `Tagged ${response.data.updated} photo(s) with rider "${rider}"` : `Cleared rider on ${response.data.updated} photo(s)`);
      setSelected(new Set());
      setBulkRider("");
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function applyBulkStatus(isActive) {
    if (isActive === false && !window.confirm(`Disable ${selected.size} selected photo(s)? They disappear from the public gallery until re-enabled.`)) return;
    setBusy("bulk-status");
    try {
      const response = await api.put("/admin/photos/bulk-status", { photo_ids: [...selected], is_active: isActive });
      toast.success(`${isActive ? "Enabled" : "Disabled"} ${response.data.updated} photo(s)`);
      setSelected(new Set());
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function aiLabelSelected() {
    if (!window.confirm(`AI label ${selected.size} selected photo(s)? This overwrites their existing titles, vehicles and tags.`)) return;
    setLabeling("bulk");
    let done = 0;
    let failed = 0;
    const targets = [...selected];
    for (const recordId of targets) {
      setLabelProgress(`${done + failed + 1}/${targets.length}`);
      try {
        await api.post(`/admin/photos/${recordId}/ai-label`);
        done += 1;
      } catch {
        failed += 1;
      }
    }
    setLabeling("");
    setLabelProgress("");
    toast.success(`AI labeled ${done} photo(s)${failed ? `, ${failed} failed` : ""}`);
    setSelected(new Set());
    await fetchMedia();
    onChanged?.();
  }

  const tableRows = useMemo(() => {
    const rows = [];
    let lastEvent = null;
    const sorted = [...filtered].sort((a, b) => (a.event_title || "").localeCompare(b.event_title || ""));
    sorted.forEach((item) => {
      if (item.event_title !== lastEvent) {
        rows.push({ type: "header", key: `header-${item.event_id}`, title: item.event_title });
        lastEvent = item.event_title;
      }
      rows.push({ type: "item", key: rowKey(item), item });
    });
    return rows;
  }, [filtered]);

  async function submitUpload(event) {
    event.preventDefault();
    if (!upload.file) return toast.error("Choose a file first");
    setBusy("upload");
    try {
      const data = new FormData();
      data.append("event_id", upload.event_id);
      data.append("title", upload.title);
      data.append("file", upload.file);
      await api.post("/admin/media", data);
      toast.success("Media uploaded to storage");
      setUpload({ event_id: upload.event_id, title: "", file: null });
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function toggleStatus(item) {
    const key = rowKey(item);
    setBusy(key);
    try {
      await api.patch(`/admin/media/${item.record_type}/${item.record_id}/status`, { is_active: item.status !== "active" });
      toast.success(item.status === "active" ? "Media disabled" : "Media enabled");
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function replaceMedia(item, file) {
    const key = rowKey(item);
    setBusy(key);
    try {
      const data = new FormData();
      data.append("file", file);
      await api.post(`/admin/media/${item.record_type}/${item.record_id}/replace`, data);
      toast.success("Media replaced");
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function purgeMedia(item) {
    if (!window.confirm(`Purge "${item.title}"? The record is removed and all access is disabled. Storage retains an inaccessible copy.`)) return;
    const key = rowKey(item);
    setBusy(key);
    try {
      await api.delete(`/admin/media/${item.record_type}/${item.record_id}`);
      toast.success("Media purged and access disabled");
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function aiLabel(item) {
    const key = rowKey(item);
    setLabeling(key);
    try {
      const response = await api.post(`/admin/photos/${item.record_id}/ai-label`);
      toast.success(`AI labeled: "${response.data.title}" · ${response.data.vehicle}`);
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error, "AI labeling failed"));
    } finally {
      setLabeling("");
    }
  }

  async function aiLabelAll() {
    const targets = items.filter((item) => item.record_type === "photo" && item.role === "original" && !item.ai_labeled);
    if (!targets.length) return toast.info("Every photo is already AI labeled");
    setLabeling("all");
    let done = 0;
    let failed = 0;
    for (const item of targets) {
      setLabelProgress(`${done + failed + 1}/${targets.length}`);
      try {
        await api.post(`/admin/photos/${item.record_id}/ai-label`);
        done += 1;
      } catch {
        failed += 1;
      }
    }
    setLabeling("");
    setLabelProgress("");
    toast.success(`AI labeled ${done} photo(s)${failed ? `, ${failed} failed` : ""}`);
    await fetchMedia();
    onChanged?.();
  }

  async function setCover(item) {
    const key = rowKey(item);
    setBusy(key);
    try {
      await api.put(`/admin/events/${item.event_id}/cover`, { photo_id: item.record_id });
      toast.success(`"${item.title}" is now the cover for ${item.event_title}`);
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  function startLabelEdit(item) {
    setLabelEditId(item.record_id);
    setLabelDraft({ title: item.title, vehicle: item.vehicle || "", tags: (item.tags || []).join(", ") });
  }

  async function saveLabels(item) {
    const title = labelDraft.title.trim();
    if (!title) return toast.error("Title is required");
    const tags = labelDraft.tags.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean).slice(0, 8);
    const key = rowKey(item);
    setBusy(key);
    try {
      await api.put(`/admin/photos/${item.record_id}`, { title, vehicle: labelDraft.vehicle.trim(), tags });
      toast.success(`Labels updated for "${title}"`);
      setLabelEditId("");
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy("");
    }
  }

  async function savePhotoMeta(item) {
    const price = Number(priceDrafts[item.record_id] ?? item.price);
    const rider = riderDrafts[item.record_id] ?? item.rider ?? "";
    if (!price || price <= 0) return toast.error("Enter a valid price");
    try {
      await api.put(`/admin/photos/${item.record_id}`, { price, rider });
      toast.success(`"${item.title}" updated — $${price.toFixed(2)}${rider ? ` · rider ${rider}` : ""}`);
      await fetchMedia();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  return (
    <section data-testid="media-library-section" className="mt-12 border border-white/10 bg-carbon p-6">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <h2 className="font-display text-3xl text-white">Media library</h2>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-zinc-500">Pick an album to view and edit its photos one batch at a time. Search within the album, download originals or previews, replace, enable or disable, set prices and labels, and choose which photo becomes the event cover. Purged records lose all access; the storage provider retains an inaccessible copy.</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 lg:w-auto">
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger data-testid="media-event-filter" className="h-11 w-52 border-white/10 bg-white/[0.03]"><SelectValue placeholder="Choose an album" /></SelectTrigger>
            <SelectContent>
              {events.map((eventItem) => <SelectItem key={eventItem.id} value={eventItem.id}>{eventItem.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="ai-label-all-button" disabled={Boolean(labeling)} onClick={aiLabelAll} className="inline-flex items-center gap-2 border border-gold/40 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-onyx disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> {labeling === "all" ? `Labeling ${labelProgress}` : "AI label all"}
          </button>
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
            <Input data-testid="media-search-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, event, file or type" className="h-11 border-white/10 bg-white/[0.03] pl-10" />
          </div>
        </div>
      </div>

      <form data-testid="media-upload-form" onSubmit={submitUpload} className="mt-8 grid gap-4 border border-gold/15 bg-white/[0.02] p-5 md:grid-cols-[1fr_1fr_1.4fr_auto] md:items-center">
        <Select value={upload.event_id} onValueChange={(value) => setUpload({ ...upload, event_id: value })}>
          <SelectTrigger data-testid="media-upload-event-select" className="border-white/10 bg-white/[0.03]"><SelectValue placeholder="Choose event" /></SelectTrigger>
          <SelectContent>{events.map((eventItem) => <SelectItem key={eventItem.id} value={eventItem.id}>{eventItem.title}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="media-upload-title-input" required placeholder="Media title" value={upload.title} onChange={(event) => setUpload({ ...upload, title: event.target.value })} className="border-white/10 bg-white/[0.03]" />
        <Input data-testid="media-upload-file-input" required type="file" accept={ACCEPT} onChange={(event) => setUpload({ ...upload, file: event.target.files?.[0] || null })} className="border-white/10 bg-white/[0.03]" />
        <Button data-testid="media-upload-submit-button" disabled={busy === "upload"} className="rounded-none bg-gold font-mono text-xs uppercase tracking-[0.2em] text-onyx hover:bg-titanium">
          <UploadCloud className="h-4 w-4" /> {busy === "upload" ? "Uploading" : "Upload"}
        </Button>
      </form>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">JPG · PNG · WebP up to 30MB · MP4 · MOV · WebM · PDF · ZIP · MP3 · WAV up to 150MB</p>

      {selected.size > 0 && (
        <div data-testid="media-bulk-price-toolbar" className="mt-4 flex flex-wrap items-center gap-3 border border-gold/30 bg-gold/[0.06] p-4">
          <span data-testid="media-bulk-price-count" className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{selected.size} photo(s) selected</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">$</span>
            <Input data-testid="media-bulk-price-input" type="number" min="1" step="0.01" placeholder="4.99" value={bulkPrice} onChange={(event) => setBulkPrice(event.target.value)} className="h-9 w-28 border-white/10 bg-white/[0.03] text-xs" />
          </div>
          <button data-testid="media-bulk-price-apply" disabled={Boolean(busy) || Boolean(labeling)} onClick={applyBulkPrice} className="border border-gold/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx disabled:opacity-50">{busy === "bulk-price" ? "Applying" : "Apply price"}</button>
          <div className="flex items-center gap-2">
            <Input data-testid="media-bulk-rider-input" placeholder="Rider / driver" value={bulkRider} onChange={(event) => setBulkRider(event.target.value)} className="h-9 w-36 border-white/10 bg-white/[0.03] text-xs" />
            <button data-testid="media-bulk-rider-apply" disabled={Boolean(busy) || Boolean(labeling)} onClick={applyBulkRider} className="whitespace-nowrap border border-gold/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx disabled:opacity-50">Tag rider</button>
          </div>
          <button data-testid="media-bulk-enable" disabled={Boolean(busy) || Boolean(labeling)} onClick={() => applyBulkStatus(true)} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-50">Enable</button>
          <button data-testid="media-bulk-disable" disabled={Boolean(busy) || Boolean(labeling)} onClick={() => applyBulkStatus(false)} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-50">Disable</button>
          <button data-testid="media-bulk-ai-label" disabled={Boolean(labeling) || Boolean(busy)} onClick={aiLabelSelected} className="inline-flex items-center gap-2 border border-gold/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx disabled:opacity-50">
            <Sparkles className="h-3 w-3" /> {labeling === "bulk" ? `Labeling ${labelProgress}` : "AI label"}
          </button>
          <button data-testid="media-bulk-price-clear" onClick={() => setSelected(new Set())} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 transition-colors hover:border-gold/50 hover:text-gold">Clear</button>
        </div>
      )}

      <div data-testid="media-library-table" className="mt-8 overflow-x-auto">
        {loading && <p data-testid="media-loading-state" className="p-6 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Loading media library</p>}
        {!loading && filtered.length === 0 && <p data-testid="media-empty-state" className="border border-white/10 p-8 text-sm text-zinc-500">{query.trim() ? "No media matches your search in this album." : "No media in this album yet."}</p>}
        {!loading && filtered.length > 0 && (
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              <tr><th className="p-3"><input data-testid="media-select-all" type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-4 w-4 accent-gold" /></th><th className="p-3">Preview</th><th className="p-3">File</th><th className="p-3">Event</th><th className="p-3">Type</th><th className="p-3">Size</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
            </thead>
            <tbody>
              {tableRows.map((row) => {
                if (row.type === "header") {
                  return (
                    <tr key={row.key} data-testid={`media-group-${row.title}`}>
                      <td colSpan={8} className="bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{row.title}</td>
                    </tr>
                  );
                }
                const item = row.item;
                const key = row.key;
                const working = busy === key;
                const isPhotoOriginal = item.record_type === "photo" && item.role === "original";
                return (
                  <tr key={key} data-testid={`media-row-${key}`} className={`border-t border-white/10 ${item.status !== "active" ? "opacity-50" : ""} ${selected.has(item.record_id) ? "bg-gold/[0.05]" : ""}`}>
                    <td className="p-3">
                      {isPhotoOriginal && <input data-testid={`media-select-${item.record_id}`} type="checkbox" checked={selected.has(item.record_id)} onChange={() => toggleSelect(item.record_id)} className="h-4 w-4 accent-gold" />}
                    </td>
                    <td className="p-3">
                      {item.is_image ? (
                        <img src={downloadUrl(item, "preview")} alt={`${item.title} thumbnail`} loading="lazy" className="h-12 w-20 border border-white/10 object-cover" />
                      ) : (
                        <span className="flex h-12 w-20 items-center justify-center border border-white/10 bg-white/[0.03] text-zinc-500"><FileBox className="h-4 w-4" /></span>
                      )}
                    </td>
                    <td className="p-3">
                      <p className="font-lux text-white">{item.title}{item.ai_labeled && <span className="ml-2 border border-gold/30 px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.14em] text-gold">AI</span>}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{item.role} · {item.original_filename}</p>
                      {item.tags?.length > 0 && <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-600">{item.tags.join(" · ")}</p>}
                      {item.record_type === "photo" && item.role === "original" && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">$</span>
                          <Input data-testid={`media-price-input-${item.record_id}`} type="number" min="1" step="0.01" value={priceDrafts[item.record_id] ?? item.price ?? ""} onChange={(event) => setPriceDrafts((current) => ({ ...current, [item.record_id]: event.target.value }))} className="h-8 w-20 border-white/10 bg-white/[0.03] text-xs" />
                          <Input data-testid={`media-rider-input-${item.record_id}`} placeholder="Rider / driver" value={riderDrafts[item.record_id] ?? item.rider ?? ""} onChange={(event) => setRiderDrafts((current) => ({ ...current, [item.record_id]: event.target.value }))} className="h-8 w-32 border-white/10 bg-white/[0.03] text-xs" />
                          <button data-testid={`media-price-save-${item.record_id}`} onClick={() => savePhotoMeta(item)} className="border border-gold/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx">Save</button>
                          <button data-testid={`media-ai-label-${item.record_id}`} disabled={Boolean(labeling)} onClick={() => aiLabel(item)} className="inline-flex items-center gap-1 border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-50">
                            <Sparkles className="h-3 w-3" /> {labeling === key ? "..." : "AI label"}
                          </button>
                          {item.is_cover ? (
                            <span data-testid={`media-cover-badge-${item.record_id}`} className="border border-gold/40 bg-gold/15 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-gold">★ Cover</span>
                          ) : (
                            <button data-testid={`media-set-cover-${item.record_id}`} disabled={working} onClick={() => setCover(item)} className="border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-50">Set cover</button>
                          )}
                          <button data-testid={`media-edit-labels-${item.record_id}`} disabled={working} onClick={() => (labelEditId === item.record_id ? setLabelEditId("") : startLabelEdit(item))} className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors disabled:opacity-50 ${labelEditId === item.record_id ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-300 hover:border-gold/50 hover:text-gold"}`}>
                            {labelEditId === item.record_id ? "Close" : "Edit labels"}
                          </button>
                        </div>
                      )}
                      {item.record_type === "photo" && item.role === "original" && labelEditId === item.record_id && (
                        <div data-testid={`media-label-form-${item.record_id}`} className="mt-3 grid gap-2 border border-gold/20 bg-white/[0.02] p-3">
                          <Input data-testid={`media-label-title-${item.record_id}`} placeholder="Photo title" value={labelDraft.title} onChange={(event) => setLabelDraft({ ...labelDraft, title: event.target.value })} className="h-9 border-white/10 bg-white/[0.03] text-xs" />
                          <Input data-testid={`media-label-vehicle-${item.record_id}`} placeholder="Vehicle / subject" value={labelDraft.vehicle} onChange={(event) => setLabelDraft({ ...labelDraft, vehicle: event.target.value })} className="h-9 border-white/10 bg-white/[0.03] text-xs" />
                          <Input data-testid={`media-label-tags-${item.record_id}`} placeholder="Search tags, comma separated" value={labelDraft.tags} onChange={(event) => setLabelDraft({ ...labelDraft, tags: event.target.value })} className="h-9 border-white/10 bg-white/[0.03] font-mono text-xs" />
                          <div className="flex gap-2">
                            <button data-testid={`media-label-save-${item.record_id}`} disabled={working} onClick={() => saveLabels(item)} className="border border-gold/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx disabled:opacity-50">Save labels</button>
                            <button data-testid={`media-label-cancel-${item.record_id}`} onClick={() => setLabelEditId("")} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 transition-colors hover:border-gold/50 hover:text-gold">Cancel</button>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-zinc-400">{item.event_title}</td>
                    <td className="p-3 font-mono text-xs text-zinc-500">{item.content_type}</td>
                    <td className="p-3 font-mono text-xs text-titanium">{formatBytes(item.size)}</td>
                    <td className="p-3">
                      <span data-testid={`media-status-${key}`} className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${item.status === "active" ? "border-gold/40 text-gold" : "border-white/15 text-zinc-500"}`}>{item.status}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <a data-testid={`media-download-${key}`} href={downloadUrl(item)} className="inline-flex items-center gap-1 border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
                          <Download className="h-3 w-3" /> Get
                        </a>
                        {item.has_preview && (
                          <a data-testid={`media-preview-${key}`} href={downloadUrl(item, "preview")} className="inline-flex items-center gap-1 border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
                            Preview
                          </a>
                        )}
                        <button data-testid={`media-toggle-${key}`} disabled={working} onClick={() => toggleStatus(item)} className="border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
                          {item.status === "active" ? "Disable" : "Enable"}
                        </button>
                        {item.role !== "preview" && (
                          <label data-testid={`media-replace-${key}`} className={`inline-flex cursor-pointer items-center gap-1 border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold ${working ? "pointer-events-none opacity-50" : ""}`}>
                            <RefreshCcw className="h-3 w-3" /> Replace
                            <input data-testid={`media-replace-input-${key}`} type="file" accept={ACCEPT} className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) replaceMedia(item, file); event.target.value = ""; }} />
                          </label>
                        )}
                        <button data-testid={`media-purge-${key}`} disabled={working} onClick={() => purgeMedia(item)} className="border border-red-500/30 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-red-300 transition-colors hover:bg-red-500/10">
                          Purge
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
